const API_BASE_URL = (import.meta.env.VITE_API_BASE_URL || '/api/v1').replace(/\/$/, '');

const TOKEN_KEY = 'secrettalk.web.token';

export interface ApiErrorPayload {
  code?: string;
  message?: string;
  ui_message?: string;
}

interface ApiEnvelope<T> {
  success: boolean;
  data: T;
  error?: ApiErrorPayload;
}

export class ApiError extends Error {
  status: number;
  code?: string;
  uiMessage?: string;

  constructor(status: number, error?: ApiErrorPayload) {
    super(error?.ui_message || error?.message || `Request failed with status ${status}`);
    this.name = 'ApiError';
    this.status = status;
    this.code = error?.code;
    this.uiMessage = error?.ui_message;
  }
}

export function getAuthToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}

export function setAuthToken(token: string) {
  localStorage.setItem(TOKEN_KEY, token);
}

export function clearAuthToken() {
  localStorage.removeItem(TOKEN_KEY);
}

export async function apiFetch<T>(path: string, init: RequestInit = {}): Promise<T> {
  const token = getAuthToken();
  const headers = new Headers(init.headers);

  if (!headers.has('Accept')) {
    headers.set('Accept', 'application/json');
  }
  if (!(init.body instanceof FormData) && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }
  if (token) {
    headers.set('Authorization', `Bearer ${token}`);
  }

  const response = await fetch(`${API_BASE_URL}${path}`, {
    ...init,
    headers,
  });

  let payload: ApiEnvelope<T> | null = null;

  try {
    payload = (await response.json()) as ApiEnvelope<T>;
  } catch {
    if (!response.ok) {
      throw new ApiError(response.status);
    }
    throw new Error('Invalid API response');
  }

  if (!response.ok || !payload.success) {
    throw new ApiError(response.status, payload.error);
  }

  return payload.data;
}

export { API_BASE_URL };
